import { useEffect, useState } from 'react';
import { updateUserInfo } from '../../../services/User_service';
import { UserModel } from '../../../models/UserModel';
import { format } from 'date-fns';
import Modal from 'react-modal';
import avatar from '../../../image/avatar.png';
import ReferralTransactions from '../ReferralTransactions';
import { Table, Button, Form, Badge, InputGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserDetails } from '../../../userThunk';
import { AppDispatch, RootState } from '../../../store';
import { FaUserFriends, FaBuilding, FaIndustry, FaIdBadge, FaUserTie } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import '../../../css/GestionClient.css';

Modal.setAppElement('#root');

interface GestionClientProps {
  parentId?: number;
  isAdmin?: boolean;
  getListUsers: () => void;
  clients: UserModel[];
  setClients: (clients: UserModel[]) => void;
}

function GestionClient({ parentId, isAdmin = false, getListUsers, clients, setClients }: GestionClientProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [newReferralPercentage, setNewReferralPercentage] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [filteredClients, setFilteredClients] = useState<UserModel[]>(clients);
  
  const authUser = useSelector((state: RootState) => state.user.user);
  const dispatch: AppDispatch = useDispatch();

  const role = localStorage.getItem("role");

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992); // Bootstrap lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!authUser) {
      dispatch(fetchUserDetails());
    }
  }, [dispatch, authUser]);

  // ==========================================
  // FILTRAGE DES CLIENTS
  // ==========================================
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client => {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${client.firstname || ''} ${client.name || ''}`.toLowerCase();
        const email = (client.email || '').toLowerCase();
        const reference = (client.reference || '').toLowerCase();
        const address = (client.adresse || '').toLowerCase();
        
        return fullName.includes(searchLower) ||
               email.includes(searchLower) ||
               reference.includes(searchLower) ||
               address.includes(searchLower);
      });
      setFilteredClients(filtered);
    }
  }, [searchTerm, clients]);

  // ==========================================
  // HANDLERS
  // ==========================================
  const handleReferralPercentageUpdate = async () => {
    if (selectedUser) {
      try {
        if (selectedUser.parent_id) {
          await updateUserInfo(selectedUser.parent_id, { referralPercentage: newReferralPercentage });
          getListUsers();
        }
        handleCloseEditModal();
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la remise parrainage", error);
        alert("Erreur lors de la mise à jour de la remise parrainage.");
      }
    }
  };

  const handleUserDetail = (user: UserModel) => {
    setSelectedUser(user);
    setModalIsOpen(true);
    setActiveTab('details');
  };

  const handleCloseDetail = () => {
    setModalIsOpen(false);
    setSelectedUser(null);
  };

  const handleOpenEditModal = (client: UserModel) => {
    setSelectedUser(client);
    setNewReferralPercentage(client.Parent?.referralPercentage || 0);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  const formatDate = (date: string | Date) => {
  return format(new Date(date), 'dd/MM/yyyy');
};


  const getUserType = (client: UserModel) => {
    if (client.is_entreprise) return 'Entreprise';
    if (client.user_type === 'professionnel') return 'Professionnel';
    return 'Particulier';
  };

  const getUserTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'Entreprise':
        return 'primary';
      case 'Professionnel':
        return 'info';
      default:
        return 'info';
    }
  };

  // ==========================================
  // VUE TABLEAU (DESKTOP > 992px)
  // ==========================================
  const renderTableView = () => (
    <div className="table-responsive">
      <Table hover className="gestion-client-table">
        <thead>
          <tr>
            <th>FILLEUL</th>
            <th>RÉFÉRENCE</th>
            <th>ADRESSE</th>
            <th>DATE D'INSCRIPTION</th>
            {isAdmin && <th>PARRAIN</th>}
            {role === 'ADMIN' && (
              <>
                <th>REMISE PARRAINAGE</th>
                <th>CODE PARRAINAGE</th>
              </>
            )}
            <th className="text-center">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {filteredClients.length === 0 ? (
            <tr>
              <td colSpan={isAdmin ? (role === 'ADMIN' ? 8 : 6) : (role === 'ADMIN' ? 7 : 5)} className="text-center text-muted py-4">
                {searchTerm ? 'Aucun client trouvé pour cette recherche' : 'Aucun client parrainé'}
              </td>
            </tr>
          ) : (
            filteredClients
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((client) => {
                const userType = getUserType(client);
                return (
                  <tr key={client.id}>
                    <td>
                      <div className="client-info">
                        <strong className="client-info-name">
                          {client.is_entreprise 
                            ? client.raison_social 
                            : `${client.firstname || ''} ${client.name || ''}`.trim()}
                        </strong>
                        <br />
                        <small className="text-muted client-info-email">
                          {client.email}
                        </small>
                        <br />
                        <Badge 
                          bg={getUserTypeBadgeColor(userType)}
                          className="mt-1"
                        >
                          {userType}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <span className="client-reference">{client.reference || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="client-address">{client.adresse || 'Non renseignée'}</span>
                    </td>
                    <td>
                      <span className="client-date">{formatDate(client.createdAt)}</span>
                    </td>
                    
                    {/* Colonne parrain pour l'admin */}
                    {isAdmin && (
                      <td>
                        {client.Parent ? (
                          <div className="parent-info">
                            <div className="fw-bold parent-name">
                              <FaUserTie className="me-1 parent-icon" style={{ marginBottom: '0.2rem' }} />
                              {client.Parent.is_entreprise 
                                ? client.Parent.raison_social 
                                : `${client.Parent.firstname} ${client.Parent.name}`
                              }
                            </div>
                            <small className="text-muted d-block">
                              Code: {client.Parent.code_parrainage}
                            </small>
                            <small className="text-muted d-block">
                              {client.Parent.email}
                            </small>
                          </div>
                        ) : (
                          <span className="text-muted">Aucun parrain</span>
                        )}
                      </td>
                    )}

                    {/* Colonnes admin */}
                    {role === 'ADMIN' && (
                      <>
                        <td>
                          <span>{client.Parent?.referralPercentage}% </span>
                          <Button 
                            variant="link" 
                            size="sm" 
                            onClick={() => handleOpenEditModal(client)}
                            className="p-0"
                          >
                            ✏️
                          </Button>
                        </td>
                        <td>{client.code_parrainage}</td>
                      </>
                    )}
                    
                    <td className="text-center">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => handleUserDetail(client)}
                        className="btn-detail"
                      >
                        Détail
                      </Button>
                    </td>
                  </tr>
                );
              })
          )}
        </tbody>
      </Table>
    </div>
  );

  // ==========================================
  // VUE CARTES (MOBILE < 992px)
  // ==========================================
  const renderCardView = () => (
    <div className="clients-cards-container">
      {filteredClients.length === 0 ? (
        <div className="empty-state text-center text-muted py-5">
          <div className="empty-state-icon mb-3">📋</div>
          <h5>{searchTerm ? 'Aucun client trouvé' : 'Aucun client parrainé'}</h5>
          <p className="text-muted">
            {searchTerm 
              ? 'Essayez une autre recherche' 
              : 'Partagez votre code de parrainage pour obtenir vos premiers clients'}
          </p>
        </div>
      ) : (
        filteredClients
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map((client, index) => {
            const userType = getUserType(client);
            return (
              <div key={client.id} className="client-card" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="client-card-header">
                  <h6 className="client-name">
                    {client.is_entreprise 
                      ? client.raison_social 
                      : `${client.firstname || ''} ${client.name || ''}`.trim()}
                  </h6>
                  <Badge bg={getUserTypeBadgeColor(userType)}>
                    {userType}
                  </Badge>
                </div>
                
                <div className="client-card-body">
                  <div className="client-card-row">
                    <span className="label">📧 Email :</span>
                    <span className="value">{client.email}</span>
                  </div>
                  
                  <div className="client-card-row">
                    <span className="label">🔖 Référence :</span>
                    <span className="value">{client.reference || 'N/A'}</span>
                  </div>
                  
                  <div className="client-card-row">
                    <span className="label">📍 Adresse :</span>
                    <span className="value">{client.adresse || 'Non renseignée'}</span>
                  </div>
                  
                  <div className="client-card-row">
                    <span className="label">📅 Inscription :</span>
                    <span className="value">{formatDate(client.createdAt)}</span>
                  </div>

                  {/* Parrain (admin uniquement) */}
                  {isAdmin && (
                    <div className="client-card-row">
                      <span className="label">👤 Parrain :</span>
                      <span className="value">
                        {client.Parent ? (
                          <>
                            {client.Parent.is_entreprise 
                              ? client.Parent.raison_social 
                              : `${client.Parent.firstname} ${client.Parent.name}`
                            }
                            <br />
                            <small className="text-muted">
                              {client.Parent.code_parrainage}
                            </small>
                          </>
                        ) : (
                          'Aucun'
                        )}
                      </span>
                    </div>
                  )}

                  {/* Info admin */}
                  {role === 'ADMIN' && (
                    <>
                      <div className="client-card-row">
                        <span className="label">💰 Remise :</span>
                        <span className="value">
                          {client.Parent?.referralPercentage}%
                          <Button 
                            variant="link" 
                            size="sm" 
                            onClick={() => handleOpenEditModal(client)}
                            className="p-0 ms-2"
                          >
                            ✏️
                          </Button>
                        </span>
                      </div>
                      <div className="client-card-row">
                        <span className="label">🎫 Code :</span>
                        <span className="value">{client.code_parrainage}</span>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="client-card-footer">
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleUserDetail(client)}
                    className="w-100 btn-detail-card"
                  >
                    Voir les détails
                  </Button>
                </div>
              </div>
            );
          })
      )}
    </div>
  );

  // ==========================================
  // RENDU PRINCIPAL
  // ==========================================
  return (
    <>
      <div className="gestion-client-container">
        {/* Barre de recherche */}
        <div className="search-bar-container mb-3">
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={isAdmin ? "Rechercher dans tous les filleuls..." : "Rechercher un client (nom, email, référence, adresse...)"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary"
                onClick={() => setSearchTerm('')}
                className="btn-clear-search"
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </div>

        {/* Stats rapides */}
        <div className="clients-stats mb-3">
          <div className="stat-item">
            <span className="stat-label">Total clients :</span>
            <span className="stat-value">{clients.length}</span>
          </div>
          {searchTerm && (
            <div className="stat-item">
              <span className="stat-label">Résultats :</span>
              <span className="stat-value">{filteredClients.length}</span>
            </div>
          )}
        </div>

        {/* Vue adaptative : tableau ou cartes */}
        {isMobile ? renderCardView() : renderTableView()}
      </div>

      {/* Modal pour modifier la remise parrainage */}
      <Modal
        isOpen={editModalOpen}
        onRequestClose={handleCloseEditModal}
        contentLabel="Modifier la remise parrainage"
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 },
          content: {
            top: '50%', left: '50%', right: 'auto', bottom: 'auto',
            marginRight: '-50%', transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : '400px', 
            maxWidth: '400px',
            height: 'auto', 
            padding: '20px', 
            borderRadius: '10px',
          },
        }}
      >
        <h4>Modifier la remise parrainage</h4>
        <Form.Group className="mt-3">
          <Form.Label>Nouvelle remise (%)</Form.Label>
          <Form.Control
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={newReferralPercentage}
            onChange={(e) => {
              const val = parseFloat(e.target.value);
              setNewReferralPercentage(isNaN(val) ? 0 : val);
            }}
          />
        </Form.Group>
        <div className="mt-3 d-flex gap-2">
          <Button variant="primary" onClick={handleReferralPercentageUpdate}>
            Enregistrer
          </Button>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Annuler
          </Button>
        </div>
      </Modal>

      {/* Modal de détails utilisateur */}
      {selectedUser && (
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={handleCloseDetail}
          contentLabel="Détails de l'utilisateur"
          style={{
            overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 },
            content: {
              top: '50%', 
              left: '50%', 
              right: 'auto', 
              bottom: 'auto',
              marginRight: '-50%', 
              transform: 'translate(-50%, -50%)',
              width: isMobile ? '95%' : '90%', 
              maxWidth: '1200px',
              height: isMobile ? '90vh' : 'auto',
              maxHeight: '90vh',
              padding: isMobile ? '15px' : '20px', 
              borderRadius: '10px',
              overflowY: 'auto',
            },
          }}
        >
          <div className="modal-detail-header">
            <button 
              onClick={handleCloseDetail}
              className="btn-close"
              style={{ 
                position: 'absolute', 
                top: '15px', 
                right: '15px',
                zIndex: 10
              }}
            />
          </div>

          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} 
                onClick={() => setActiveTab('details')}
              >
                Détails
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`} 
                onClick={() => setActiveTab('transactions')}
              >
                Transactions
              </button>
            </li>
          </ul>

          <div className="tab-content mt-3">
            {activeTab === 'details' && (
              <div>
                <div className="text-center mb-3">
                  <img src={avatar} alt="Avatar" className="avatar" style={{ width: '80px', height: '80px', borderRadius: '50%' }} />
                </div>
                
                <div className="text-center mb-3">
                  <h5 className="mb-1">
                    {selectedUser.firstname} {selectedUser.name}
                    {selectedUser.is_entreprise && (
                      <Badge className="badge-type-entreprise ms-2">Entreprise</Badge>

                    )}
                  </h5>
                  <p className="text-muted mb-0">{selectedUser.email}</p>
                </div>

                {/* Informations du parrain si c'est un admin */}
                {isAdmin && selectedUser.Parent && (
                  <div className="alert alert-info">
                    <h6><FaUserTie className="me-2" />Informations du parrain</h6>
                    <p className="mb-1">
                      <strong>Nom:</strong> {selectedUser.Parent.is_entreprise 
                        ? selectedUser.Parent.raison_social 
                        : `${selectedUser.Parent.firstname} ${selectedUser.Parent.name}`
                      }
                    </p>
                    <p className="mb-1"><strong>Email:</strong> {selectedUser.Parent.email}</p>
                    <p className="mb-0"><strong>Code de parrainage:</strong> {selectedUser.Parent.code_parrainage}</p>
                  </div>
                )}

                <hr />
                <div className="mb-3">
                  <p>
                    <FaIdBadge className="me-2 text-secondary" />
                    <strong>Code SIREN :</strong> {selectedUser.code_SIREN || 'N/A'}
                  </p>
                  <p>
                    <FaIndustry className="me-2 text-secondary" />
                    <strong>Code NAF :</strong> {selectedUser.code_NAF || 'N/A'}
                  </p>
                  <p>
                    <FaBuilding className="me-2 text-secondary" />
                    <strong>Raison Sociale :</strong> {selectedUser.raison_social || 'N/A'}
                  </p>
                </div>

                {/* Section clients parrainés */}
                {role === 'ADMIN' && !isAdmin && (
                  <>   
                    <hr className="my-4" />
                    <h5 className="mb-3">
                      <FaUserFriends className="me-2" /> Filleuls parrainés
                    </h5>

                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Filleul</th>
                            <th>Référence</th>
                            <th>Adresse</th>
                            <th>Date d'inscription</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clients
                            .filter(client => 
                              client.parent_id === selectedUser.id && 
                              client.role !== 'SECRETAIRE'
                            )
                            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                            .map(client => (
                              <tr key={client.id}>
                                <td>
                                  <div className="user__info">
                                    <div className="mx-2">
                                      <div className="user__name">{client.firstname} {client.name}</div>
                                      <div className="user__email">{client.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>{client.reference}</td>
                                <td>{client.adresse}</td>
                                <td>{formatDate(client.createdAt)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <ReferralTransactions clientId={selectedUser.id} />
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

export default GestionClient;